/**
 * Image Processing Utilities (Day-15 Module 9)
 * 
 * Features:
 * - Resize to max width 1600px
 * - JPEG compression
 * - File type detection
 * - Size estimation
 */

const MAX_WIDTH = 1600;
const MAX_HEIGHT = 1600;
const DEFAULT_QUALITY = 0.8; // 80% JPEG quality

/**
 * Process an image file - resize and compress
 * @param {File|Blob} file - The image file to process
 * @param {Object} options - Processing options
 * @returns {Promise<{blob: Blob, dataUrl: string, width: number, height: number, originalSize: number, processedSize: number}>}
 */
export async function processImage(file, options = {}) {
  const {
    maxWidth = MAX_WIDTH,
    maxHeight = MAX_HEIGHT,
    quality = DEFAULT_QUALITY,
    outputType = 'image/jpeg',
  } = options;

  const originalSize = file.size;

  // Load image into canvas
  const img = await loadImage(file);
  
  // Calculate new dimensions
  const { width, height } = calculateDimensions(img.width, img.height, maxWidth, maxHeight);
  
  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);
  
  // Convert to blob
  const blob = await canvasToBlob(canvas, outputType, quality);
  const dataUrl = canvas.toDataURL(outputType, quality);
  
  return {
    blob,
    dataUrl,
    width,
    height,
    originalSize,
    processedSize: blob.size,
    compressionRatio: ((1 - blob.size / originalSize) * 100).toFixed(1),
    mimeType: outputType,
  };
}

/**
 * Load an image file into an Image element
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    
    // Handle both File and Blob
    if (file instanceof Blob) {
      img.src = URL.createObjectURL(file);
    } else {
      reject(new Error('Invalid file type'));
    }
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 */
function calculateDimensions(origWidth, origHeight, maxWidth, maxHeight) {
  // If image is smaller than max, return original dimensions
  if (origWidth <= maxWidth && origHeight <= maxHeight) {
    return { width: origWidth, height: origHeight };
  }
  
  // Calculate ratio
  const widthRatio = maxWidth / origWidth;
  const heightRatio = maxHeight / origHeight;
  const ratio = Math.min(widthRatio, heightRatio);
  
  return {
    width: Math.round(origWidth * ratio),
    height: Math.round(origHeight * ratio),
  };
}

/**
 * Convert canvas to Blob
 */
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      type,
      quality
    );
  });
}

/**
 * Create a thumbnail from an image
 */
export async function createThumbnail(file, size = 200) {
  const result = await processImage(file, {
    maxWidth: size,
    maxHeight: size,
    quality: 0.7,
  });
  return result;
}

/**
 * Check if a file is an image
 */
export function isImageFile(file) {
  return file && file.type && file.type.startsWith('image/');
}

/**
 * Get file extension from mime type
 */
export function getExtensionFromMime(mimeType) {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  };
  return mimeToExt[mimeType] || 'bin';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Generate a unique filename
 */
export function generateFilename(originalName, prefix = 'attachment') {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const ext = originalName ? originalName.split('.').pop() : 'jpg';
  return `${prefix}_${timestamp}_${random}.${ext}`;
}

/**
 * Convert File to base64 data URL
 */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert base64 data URL to Blob
 */
export function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mimeType });
}

/**
 * Validate image dimensions
 */
export async function validateImageDimensions(file, minWidth = 100, minHeight = 100) {
  const img = await loadImage(file);
  return {
    valid: img.width >= minWidth && img.height >= minHeight,
    width: img.width,
    height: img.height,
    message: img.width < minWidth || img.height < minHeight
      ? `Image too small. Minimum size: ${minWidth}x${minHeight}px`
      : null,
  };
}

export default {
  processImage,
  createThumbnail,
  isImageFile,
  getExtensionFromMime,
  formatFileSize,
  generateFilename,
  fileToDataUrl,
  dataUrlToBlob,
  validateImageDimensions,
};
